import React, { useMemo, useState } from 'react';

function startOfMonth(date) {
  const d = new Date(date.getFullYear(), date.getMonth(), 1);
  return d;
}

function daysInMonth(date) {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
}

function pad(n) { return String(n).padStart(2, '0'); }

// Dummy scheduled posts keyed by YYYY-MM-DD
const MOCK_POSTS = {
  '2025-10-03': [
    { t: '09:00', ch: 'IG', title: 'Teaser reel: Chapter 1' },
    { t: '12:30', ch: 'X',  title: 'Thread: writing process' },
  ],
  '2025-10-06': [
    { t: '18:00', ch: 'YT', title: 'Live read premiere' },
  ],
  '2025-10-12': [
    { t: '10:00', ch: 'FB', title: 'Behind the scenes photos' },
    { t: '17:45', ch: 'IG', title: 'Carousel: characters' },
  ],
  '2025-10-18': [
    { t: '08:30', ch: 'IG', title: 'Quote card' },
    { t: '11:15', ch: 'X',  title: 'Poll: next scene' },
    { t: '20:00', ch: 'YT', title: 'Trailer drop' },
  ],
  '2025-10-25': [
    { t: '13:00', ch: 'FB', title: 'Event invite' },
  ],
};

const channelColor = (ch) => ({
  IG: '#d946ef', // pink
  X:  '#22d3ee', // cyan
  YT: '#ef4444', // red
  FB: '#60a5fa', // blue
}[ch] || '#9ca3af');

export default function PublishPage() {
  const [cursor, setCursor] = useState(() => new Date());

  // Ensure at least 3 posts per day across different platforms by auto-filling
  const getPostsForDate = (ymd) => {
    const base = (MOCK_POSTS[ymd] || []).slice();
    const used = new Set(base.map((p) => p.ch));
    const channels = ['IG', 'X', 'YT', 'FB'];
    const times = ['09:00', '13:00', '18:00', '20:00'];

    const titleFor = (ch, idx) => {
      const n = idx % 2;
      if (ch === 'IG') return n ? 'Carousel drop' : 'Reel highlight';
      if (ch === 'X')  return n ? 'Thread insights' : 'Poll: readers weigh in';
      if (ch === 'YT') return n ? 'Premiere teaser' : 'Episode upload';
      if (ch === 'FB') return n ? 'Event reminder' : 'Photo post';
      return 'Update';
    };

    // Stable but varied starting point based on date
    const seed = ymd.split('-').reduce((a, v) => a * 31 + parseInt(v, 10), 7);
    let timeIdx = seed % times.length;
    let chIdx = seed % channels.length;

    while (base.length < 3) {
      const ch = channels[chIdx % channels.length];
      chIdx++;
      if (used.has(ch)) continue; // keep platforms different
      used.add(ch);
      const t = times[timeIdx % times.length];
      timeIdx++;
      base.push({ t, ch, title: titleFor(ch, timeIdx) });
    }

    // Keep height in check
    return base.slice(0, 4);
  };

  const meta = useMemo(() => {
    const start = startOfMonth(cursor);
    const total = daysInMonth(cursor);
    const firstDow = start.getDay(); // 0-6 (Sun-Sat)
    // Build a 6-row (max) calendar grid (Sun..Sat)
    const cells = [];
    for (let i = 0; i < firstDow; i++) cells.push(null);
    for (let d = 1; d <= total; d++) cells.push(new Date(cursor.getFullYear(), cursor.getMonth(), d));
    while (cells.length % 7 !== 0) cells.push(null);
    const weeks = [];
    for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));

    const ym = `${cursor.getFullYear()}-${pad(cursor.getMonth() + 1)}`;
    return { weeks, ym };
  }, [cursor]);

  const prevMonth = () => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1));
  const nextMonth = () => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1));

  // Dark UI inline styles to match app feel
  const colors = {
    surface: '#0f172a',
    surfaceAlt: '#111827',
    border: 'rgba(255,255,255,0.08)',
    text: '#e5e7eb',
    sub: '#9aa0a6',
    accent: '#6c5ce7',
  };

  // Compact layout tuned for laptop screens
  const wrap = { maxWidth: 1056, margin: '0 0 0 6px', padding: 8, color: colors.text };
  const card = { background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 8, padding: 8 };
  const head = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 };
  const btn = { padding: '3px 6px', borderRadius: 6, border: `1px solid ${colors.border}`, background: colors.surfaceAlt, color: colors.text, cursor: 'pointer', fontSize: 12 };
  const legend = { display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' };
  const tag = (bg) => ({ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '2px 6px', borderRadius: 999, background: 'rgba(255,255,255,0.06)', border: `1px solid ${colors.border}`, color: colors.text, fontSize: 10 });
  const dot = (bg) => ({ width: 5, height: 5, borderRadius: 999, background: bg });
  const grid = { display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4 };
  const cell = { background: colors.surfaceAlt, border: `1px solid ${colors.border}`, borderRadius: 8, minHeight: 92, padding: 4, display: 'flex', flexDirection: 'column', gap: 3 };
  const dateChip = { alignSelf: 'flex-end', fontSize: 10, color: colors.sub };
  const item = (bg) => ({ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 4, alignItems: 'center', background: 'rgba(255,255,255,0.04)', border: `1px solid ${colors.border}`, borderRadius: 6, padding: '3px 5px' });
  const chBadge = (bg, label) => (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <span style={{ ...dot(bg) }} />
      <span style={{ color: colors.text, fontWeight: 600 }}>{label}</span>
    </div>
  );

  const monthLabel = cursor.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });

  return (
    <div style={wrap}>
      <div style={card}>
        {/* Header */}
        <div style={head}>
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={btn} onClick={prevMonth} aria-label="Previous month">◀</button>
            <div style={{ alignSelf: 'center', fontWeight: 700 }}>{monthLabel}</div>
            <button style={btn} onClick={nextMonth} aria-label="Next month">▶</button>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={btn} onClick={() => alert('Mock: open schedule dialog')}>New schedule (mock)</button>
          </div>
        </div>

        {/* Legend */}
        <div style={{ ...legend, marginBottom: 12 }}>
          <span style={{ color: colors.sub, fontSize: 12 }}>Channels:</span>
          <span style={tag(channelColor('IG'))}>{chBadge(channelColor('IG'), 'IG')}</span>
          <span style={tag(channelColor('X'))}>{chBadge(channelColor('X'), 'X')}</span>
          <span style={tag(channelColor('YT'))}>{chBadge(channelColor('YT'), 'YT')}</span>
          <span style={tag(channelColor('FB'))}>{chBadge(channelColor('FB'), 'FB')}</span>
        </div>

    {/* Weekday headers */}
        <div style={{ ...grid, marginBottom: 3 }}>
          {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map((w) => (
      <div key={w} style={{ textAlign: 'center', color: colors.sub, fontSize: 10 }}>{w}</div>
          ))}
        </div>

        {/* Calendar grid */}
        <div style={grid}>
          {meta.weeks.flat().map((d, i) => {
            if (!d) return <div key={`e${i}`} style={{ visibility: 'hidden' }} />;
            const ymd = `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`;
            const posts = getPostsForDate(ymd); // at least 3, cap to 4
            return (
              <div key={ymd} style={cell}>
                <div style={dateChip}>{d.getDate()}</div>
                <div style={{ display: 'grid', gap: 4 }}>
                  {posts.map((p, idx) => (
                    <div key={idx} style={item(channelColor(p.ch))} title={`${p.ch} • ${p.t}`}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ ...dot(channelColor(p.ch)) }} />
                        <span style={{ fontSize: 10, color: colors.sub }}>{p.t}</span>
                        <span style={{ fontSize: 10, color: channelColor(p.ch), fontWeight: 700 }}>{p.ch}</span>
                      </div>
                      <div style={{ fontSize: 11, color: colors.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.title}</div>
                    </div>
                  ))}
                  {posts.length === 0 && (
                    <div style={{ fontSize: 10, color: colors.sub, textAlign: 'center' }}>No posts</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
